import { useInfiniteQueryBuilder, useMutationBuilder, useQueryBuilder } from "src/query/useBuilders";
import { customFormFieldsApi } from "src/services/settings/customFormFields";
import {
  CUSTOM_FORM_FIELD_CREATE,
  CUSTOM_FORM_FIELD_DELETE,
  CUSTOM_FORM_FIELD_DETAIL,
  CUSTOM_FORM_FIELD_UPDATE,
  CUSTOM_FORM_FIELDS,
} from "../queryKeys";
import {
  CreateCustomFormFieldRequest,
  CustomFormField,
  SearchFormFieldsResponse,
  UpdateCustomFormFieldInput,
} from "src/types/settings/customFormFields";

const PAGE_LIMIT = 50;

export const useCustomFormFieldsQuery = (workspaceId: string) => {
  const result = useInfiniteQueryBuilder(
    [CUSTOM_FORM_FIELDS, workspaceId],
    ({ pageParam }: { pageParam: number }) =>
      customFormFieldsApi.searchFormFields({
        workspace: workspaceId,
        defaultFields: false,
        query: "",
        ignoreTypes: [],
        page: pageParam,
        limit: PAGE_LIMIT,
        sort: "created_date",
        order: "DESC",
      }),
    0,
    (lastPage: SearchFormFieldsResponse) => {
      const records = lastPage?.records ?? [];
      if (records.length < PAGE_LIMIT) return undefined;
      return (lastPage?.page ?? 0) + 1;
    },
    undefined,
    !!workspaceId,
  );

  const fields: CustomFormField[] = (result.data?.pages ?? []).flatMap(
    (page: SearchFormFieldsResponse) => page?.records ?? [],
  );

  return { ...result, fields };
};

export const useCustomFormFieldDetailQuery = (id: string) => {
  return useQueryBuilder(
    [CUSTOM_FORM_FIELD_DETAIL, id],
    () => customFormFieldsApi.fetchFormFieldDetail(id),
    false,
    0,
    false,
    undefined,
    !!id,
  );
};

export const useCreateCustomFormFieldMutation = () => {
  return useMutationBuilder<CustomFormField, CreateCustomFormFieldRequest>(
    [CUSTOM_FORM_FIELD_CREATE],
    (data) => customFormFieldsApi.createFormField(data),
    [], // cache updated manually via setQueryData — no auto-refetch
  );
};

export const useUpdateCustomFormFieldMutation = () => {
  return useMutationBuilder<CustomFormField, UpdateCustomFormFieldInput>(
    [CUSTOM_FORM_FIELD_UPDATE],
    ({ id, data }) => customFormFieldsApi.updateFormField(id, data),
    [CUSTOM_FORM_FIELDS],
  );
};

export const useDeleteCustomFormFieldMutation = () => {
  return useMutationBuilder<void, string>(
    [CUSTOM_FORM_FIELD_DELETE],
    (id) => customFormFieldsApi.deleteFormField(id),
    [CUSTOM_FORM_FIELDS],
  );
};
